import { useState, useEffect, useRef } from "react";
import type { GeoResult } from "../weather";
import { geocode } from "../weather";
import { colors, fonts } from "../tokens";

interface Props {
  onSelect: (result: GeoResult) => void;
}

export function LocationInput({ onSelect }: Props) {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState<GeoResult[]>([]);
  const [showSug, setShowSug] = useState(false);
  const [selected, setSelected] = useState<GeoResult | null>(null);
  const [error, setError] = useState("");
  const debRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (input.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    debRef.current = setTimeout(async () => {
      try {
        const r = await geocode(input.trim());
        setSuggestions(r);
        setShowSug(r.length > 0);
      } catch {
        setSuggestions([]);
      }
    }, 350);
  }, [input]);

  const pick = (s: GeoResult) => {
    setSelected(s);
    setInput(s.display);
    setShowSug(false);
    setError("");
    onSelect(s);
  };

  const submit = () => {
    if (selected) {
      onSelect(selected);
    } else if (suggestions.length > 0) {
      pick(suggestions[0]);
    } else {
      setError("Type a city and select from the dropdown.");
    }
  };

  return (
    <div style={{ position: "relative", maxWidth: 500 }}>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1, position: "relative" }}>
          <input
            value={input}
            onChange={(e) => { setInput(e.target.value); setSelected(null); setError(""); }}
            onFocus={() => suggestions.length > 0 && setShowSug(true)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") setShowSug(false); }}
            placeholder="Type a city name..."
            style={{
              width: "100%", padding: "9px 12px", background: colors.surface,
              border: `1px solid ${selected ? colors.green + "66" : colors.border}`,
              borderRadius: 6, color: colors.text, fontSize: 13, outline: "none", boxSizing: "border-box",
            }}
          />
          {selected && (
            <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
              fontSize: 9, color: colors.green, fontFamily: fonts.mono }}>✓ Located</span>
          )}
        </div>
        <button onClick={submit} disabled={!input.trim()}
          style={{
            padding: "9px 18px", background: input.trim() ? colors.amber : colors.border,
            color: input.trim() ? colors.bg : colors.muted, border: "none", borderRadius: 6,
            fontWeight: 600, fontSize: 12, cursor: input.trim() ? "pointer" : "default",
          }}>
          Score →
        </button>
      </div>

      {showSug && suggestions.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 72, marginTop: 3,
          background: colors.surface, border: `1px solid ${colors.borderHi}`,
          borderRadius: 6, overflow: "hidden", zIndex: 50, boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
        }}>
          {suggestions.map((s, i) => (
            <button key={i} onClick={() => pick(s)}
              onMouseEnter={(e) => (e.currentTarget.style.background = colors.raised)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              style={{
                display: "block", width: "100%", padding: "9px 12px", background: "transparent",
                border: "none", borderBottom: `1px solid ${colors.border}`,
                color: colors.text, fontSize: 12, textAlign: "left", cursor: "pointer",
              }}>
              {s.display}
              <span style={{ float: "right", fontSize: 9, color: colors.muted, fontFamily: fonts.mono }}>
                {s.lat.toFixed(2)}, {s.lng.toFixed(2)}
              </span>
            </button>
          ))}
        </div>
      )}

      {!selected && !showSug && !input && (
        <p style={{ fontSize: 10, color: colors.muted, marginTop: 6 }}>
          Enter a city name and select from the dropdown. Uses default project settings.
        </p>
      )}
      {error && <p style={{ fontSize: 10, color: colors.red, marginTop: 4 }}>{error}</p>}
    </div>
  );
}
